import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { QualityType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { BOX_SERIES_TYPE, PUBLISHED_LISTB } from '@/common/constants/common';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useParams } from 'react-router-dom';
import {
	useGetUserTreasureQuery,
	useUpdateUserTreasureMutation,
} from '@/api/user/userApiSlice';
import {
	UserTreasureFilter,
	UserTreasureRecordDetailDTO,
} from '@/api/DTO/user/userList.interface';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import PublishedChip from '@/common/components/PublishedChip';

interface Props {}

export default function AddTreasureDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);
	const [updateUserTreasure] = useUpdateUserTreasureMutation();
	const { id } = useParams();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];
	const defaultPropertyFilter: UserTreasureFilter = {
		pagination: initialPagination,
		sortings: [],
		isHad: 0,
		userId: id,
	};
	const defaultValues: DefaultValues<UserTreasureFilter> =
		defaultPropertyFilter;
	const methods = useForm<UserTreasureFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetUserTreasureQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		const response = await updateUserTreasure({
			arr: selectedData,
			userId: id || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.treasureAddSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const columns: GridColDef[] = [
		{
			field: 'gameType',
			headerName: t('box.typeName'),
			flex: 10,
			renderCell: (params: any) =>
				showLabel(params.row.gameType, BOX_SERIES_TYPE),
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 10,
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 10,
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'costPoints',
			headerName: t('chartletList.integral'),
			flex: 10,
		},
		{
			field: 'published',
			headerName: t('box.treasureStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('user.pushedTime'),
			flex: 10,
			renderCell: (params: any) => {
				if (
					params.row.publishedStartAt == '-' &&
					params.row.publishedEndAt == '-'
				) {
					return '-';
				}
				if (
					params.row.publishedStartAt != '-' &&
					params.row.publishedEndAt != '-'
				) {
					return `${params.row.publishedStartAt} 至 ${params.row.publishedEndAt}`;
				}
				if (
					params.row.publishedStartAt != '-' &&
					params.row.publishedEndAt == '-'
				) {
					return `${params.row.publishedStartAt} 至永久`;
				}
			},
		},
		// {
		// 	field: 'updatedAt',
		// 	headerName: t('user.lastUpdateTime'),
		// 	flex: 10,
		// },
		// {
		// 	field: 'createdAt',
		// 	headerName: t('user.createTime'),
		// 	flex: 10,
		// },
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		console.log(rows);
		setSelectedData(rows || []);
	};

	const transformResponse = useCallback(
		(response?: UserTreasureRecordDetailDTO[]) => {
			if (!response) return;
			const newResponse: any[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					publishedStartAt: !item.publishedStartAt
						? '-'
						: dayjs(item.publishedStartAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedEndAt: !item.publishedEndAt
						? '-'
						: dayjs(item.publishedEndAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});

			return newResponse;
		},
		[]
	);

	const formContent = (
		<>
			<Button
				component='label'
				sx={{ height: 24 }}
				color='secondary'
				endIcon={<AddCircleOutlineIcon />}
				onClick={() => handleOpen()}
			>
				{t('button.add')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('weekTreasure.assignTreasure')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<Grid container rowSpacing={2} columnSpacing={1}>
							<Grid item xs={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
									onchange={(text) => {
										handlechange(text);
									}}
								/>
							</Grid>
						</Grid>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.gameTreasureId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedData.map((item: string) => item)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.add')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
